/* eslint-disable max-lines */
//
// [ ] 抽离动画循环逻辑到单独的文件
//
import styles from './timeline.module.less';
import EventHandler, { EVENT_TYPE_MAP, type EventDefinition } from './core/EventHandler';
import Action, { type ActionDefinition } from './core/Action';
import Dataset from './core/Dataset';
import PointScale, { type Point } from './core/PointScale';
import { capitalToDash, uid } from './util/common';
import { debug } from './util/debug';
import { type Option, THEME, SPEED_TEXT_MAP } from './option';
import * as buildDomHelper from './helper/buildDom';

export type TimeValue = string | number;

export type TimeDataItem = TimeValue | { type: Point['type']; value: TimeValue };

/** 一帧持续的时间 ms */
const DURATION_OF_A_FRAME = 16;
/** 等待 DOM 渲染完成的时间 */
const PENDING_DOM_LAYOUT_COMPLETED = 2e2;
/** 强制执行的间隔 */
const ENFORCE_EVERY_INTERVAL = 1e2;
const MOBILE_PADDING_OUTER: [number, number] = [6.5, 6.5];
const AINVEST_PADDING: [number, number] = [16, 16];
const PC_PADDING_OUTER: [number, number] = [8, 8];

/** 时间轴 */
export default class Timeline extends EventHandler {
  /** 组件类型 */
  static readonly type: string = 'timeline';

  protected _type: string = Timeline.type;

  /** 两次消费之间的时间间隔(毫秒) */
  static readonly CONSUME_INTERVAL = DURATION_OF_A_FRAME;

  /** dom 容器 */
  protected _$dom: HTMLElement;

  /** 配置项 */
  protected _option: Option;

  /** 数据集管理 */
  protected _dataset: Dataset;

  /** 比例尺 */
  protected _scale: PointScale;

  /** 交互行为管理 */
  protected _action: Action;

  width = 0;

  height = 0;

  /** 时间轴有效宽度（进度条区域） */
  protected _axisWidth = 0;

  /** 超时器缓存 */
  private __timers: {
    [key: string]: { timer: number | null } | null;
  } = {};

  /** 初始化标识 */
  protected _initialized = false;

  /** 播放队列(将需要播放的时间数据索引放入队列中，然后每次只取最后一个播放，同时清空队列) */
  private __playQueue: (Pick<
    Parameters<EventDefinition[typeof EVENT_TYPE_MAP.CHANGE]>[0],
    'causeBy' | 'actionId'
  > & {
    dataIndex: number;
    callback?: () => void;
  })[] = [];

  /** 消费队列循环超时器 */
  private __loopTimer: ReturnType<typeof setTimeout> | null;

  /** 播放等待超时器 */
  private __pendingTimer: ReturnType<typeof setTimeout> | null;

  /** 是否播放状态 */
  protected _playing = false;

  /** 临时的储存区 */
  private __tempStorage = new Map<string, unknown>();

  /** 倍速 */
  private __speed: Option['config']['animation']['speed'] = String(
    Number(SPEED_TEXT_MAP[1])
  ) as Option['config']['animation']['speed'];

  constructor($dom: string | HTMLElement, option: Option) {
    super();

    if (typeof $dom === 'string') {
      this._$dom = document.getElementById(String($dom).replace(/^#*/g, ''));
    } else {
      this._$dom = $dom;
    }
    if (!this._$dom) {
      throw new Error(`dom element "#${String($dom).replace(/^#*/g, '')}" does not exist.`);
    }

    // FIXME deppClone
    this._option = { ...option };

    this._dataset = new Dataset(this, option.data, option.config?.dataIndexRange);
    this._scale = new PointScale();
    this._action = new Action(this);

    this.__loopTimer = null;
    this.__pendingTimer = null;

    this._initialize();
  }

  /**
   * 初始化
   */
  protected _initialize() {
    if (this._$dom.__timeline$instance) {
      throw new Error('The dom element has been initialized.');
    }
    this._$dom.__timeline$instance = this;

    if (
      // @ts-ignore
      ![THEME.MOBILE_SIMPLE, THEME.MOBILE_SIMPLE_DARK].includes(this._option?.theme) &&
      this._option?.config?.operation?.speed?.enable === true
    ) {
      if (
        typeof SPEED_TEXT_MAP[String(Number(this._option?.config?.animation?.speed))] !==
        'undefined'
      ) {
        // @ts-ignore
        this.__speed = String(Number(this._option?.config?.animation?.speed));
      } else if (typeof this._option?.config?.animation?.speed !== 'undefined') {
        throw new Error(
          `"config.animation.speed" must be one of: ${Object.keys(SPEED_TEXT_MAP).toString()}`
        );
      }
    }

    this._dataset.parse();
    // ! 让初始索引处于正确的范围内
    this._dataset.update({
      currentIndex:
        typeof this._option?.config?.dataIndex !== 'undefined' ? this._option?.config?.dataIndex : 0
    });

    // 是否允许在主刻度线隐藏时隐藏次刻度线
    if (this._option?.config?.axis?.tickLine?.sub?.followMainHide) {
      this._scale.followMainHide(true);
    } else {
      this._scale.followMainHide(false);
    }

    this._scale.padding([...MOBILE_PADDING_OUTER]);
    this._scale.minSubSpace(this._option?.config?.axis?.tickLine?.sub?.minSpace);
    this._scale.minMainSpace(this._option?.config?.axis?.tickLine?.main?.minSpace);
    this._scale.domain(this._dataset.getData(false));

    // * 主题样式解析
    this.__parseTheme();

    // ! 先渲染才可以操作 DOM
    this.__render();

    // HACK 首次渲染后需要一定延迟才能获取到最新的 DOM 测量数据
    // @ts-ignore
    this.__delayCallback({
      key: '_initialize',
      callback: () => {
        this._resize();

        this._initialized = true;
      },
      delay: PENDING_DOM_LAYOUT_COMPLETED
    });

    // 启动动画循环
    this.__loop();

    // ! 绑定全局事件
    this.__tempStorage.set('resizeHandler', () => {
      this.resize();
    });

    // @ts-ignore
    window.addEventListener('resize', this.__tempStorage.get('resizeHandler'));

    // ! 修复由于字体加载时机不确定导致 dom 测量不准确的问题
    try {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      document.fonts.ready.then(() => {
        this.resize();
      });
    } catch (error) {
      //
    }
  }

  private __parseTheme() {
    const classes = [styles['timeline-container'], 'js-timelineOperation'];

    // mobile ----------------------
    if (
      [
        THEME.MOBILE_WITH_VALUE_OUTER,
        THEME.MOBILE_WITH_VALUE_OUTER_DARK,
        THEME.MOBILE_COMPACT_WITH_VALUE_OUTER,
        THEME.MOBILE_COMPACT_WITH_VALUE_OUTER_DARK
        // @ts-ignore
      ].includes(this._option?.theme)
    ) {
      classes.push(styles['theme-with-value'], styles['theme-with-value-outer']);
    }

    if (
      [
        THEME.MOBILE_COMPACT,
        THEME.MOBILE_COMPACT_DARK,
        THEME.MOBILE_COMPACT_WITH_VALUE_OUTER,
        THEME.MOBILE_COMPACT_WITH_VALUE_OUTER_DARK
        // @ts-ignore
      ].includes(this._option?.theme)
    ) {
      classes.push(styles['theme-compact']);
    }

    // @ts-ignore
    if ([THEME.MOBILE_SIMPLE, THEME.MOBILE_SIMPLE_DARK].includes(this._option?.theme)) {
      classes.push(styles['theme-simple']);
    }

    if (
      [
        THEME.MOBILE_DARK,
        THEME.MOBILE_WITH_VALUE_OUTER_DARK,
        THEME.MOBILE_COMPACT_DARK,
        THEME.MOBILE_COMPACT_WITH_VALUE_OUTER_DARK,
        THEME.MOBILE_SIMPLE_DARK
        // @ts-ignore
      ].includes(this._option?.theme)
    ) {
      classes.push(styles['theme-dark']);
    }

    // pc --------------------------------
    if (
      // @ts-ignore
      [THEME.PC, THEME.PC_DARK, THEME.PC_SIMPLE, THEME.PC_SIMPLE_DARK].includes(this._option?.theme)
    ) {
      classes.push(styles['theme-pc']);
    }

    // @ts-ignore
    if ([THEME.PC, THEME.PC_DARK].includes(this._option?.theme)) {
      this._scale.padding([...PC_PADDING_OUTER] as [number, number]);
      classes.push(styles['theme-with-value'], styles['theme-with-value-outer']);
    }

    // @ts-ignore
    if ([THEME.PC_SIMPLE, THEME.PC_SIMPLE_DARK].includes(this._option?.theme)) {
      classes.push(styles['theme-pc-simple']);
    }

    // @ts-ignore
    if ([THEME.PC_DARK, THEME.PC_SIMPLE_DARK].includes(this._option?.theme)) {
      classes.push(styles['theme-pc-dark']);
    }

    // @ts-ignore
    if ([THEME.MOBILE_AINVEST_LIGHT, THEME.MOBILE_AINVEST_DARK].includes(this._option?.theme)) {
      this._scale.padding([...AINVEST_PADDING]);
      classes.push(
        // 继承刻度上下布局
        styles['theme-with-value'],
        styles['theme-with-value-outer'],
        styles['theme-compact'],

        styles['theme-mobile-ainvest-light']
      );
    }

    // @ts-ignore
    if ([THEME.MOBILE_AINVEST_DARK].includes(this._option?.theme)) {
      classes.push(styles['theme-mobile-ainvest-dark']);
    }

    // * feature
    if (this._dataset.isRange()) {
      classes.push(styles['feature-index-range']);
    }

    this._$dom.classList.add(...classes);
    // ! 保存下来在销毁时从 DOM 容器上移除
    this.__tempStorage.set('classes', classes);
  }

  /**
   * 销毁
   */
  destroy() {
    debug('destroy');

    this.__stopLoop();

    // @ts-ignore
    // ! 卸载全局事件
    window.removeEventListener('resize', this.__tempStorage.get('resizeHandler'));

    // 移除自定义样式的标签
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.__tempStorage.get('styleTag')?.remove();
    this._$dom.classList.remove(...(this.__tempStorage.get('classes') as string[]));

    this.__tempStorage.clear();

    Object.values(this.__timers)
      .filter(Boolean)
      .forEach(item => {
        clearTimeout(item.timer);
      });

    delete this._$dom.__timeline$instance;
    this._$dom.innerHTML = '';
    this._$dom = null;

    this._initialized = false;
  }

  /**
   * 延迟执行函数
   */
  private __delayCallback({
    key,
    callback,
    delay = ENFORCE_EVERY_INTERVAL,
    force,
    leading = false
  }: {
    key: string;
    callback: () => void;
    delay?: number;
    force?: number;
    leading?: boolean;
  }) {
    if (typeof callback !== 'function') {
      return this;
    }

    if (this.__timers[key] && this.__timers[key].timer) {
      // 节流
      if (force) {
        return this;
      }

      clearTimeout(this.__timers[key].timer);
      this.__timers[key].timer = null;
    }

    // 节流、且先执行后等待
    if (force && leading) {
      callback();
      this.__timers[key] = {
        timer: setTimeout(() => {
          this.__timers[key].timer = null;
        }, Math.min(force, delay))
      };

      return this;
    }

    this.__timers[key] = {
      timer: setTimeout(
        () => {
          callback();
          this.__timers[key].timer = null;
        },
        force && !leading ? Math.min(force, delay) : delay
      )
    };

    return this;
  }

  /**
   * 获取当前数据索引位置
   *
   * @version v0.0.2+
   */
  getCurrentIndex() {
    return this._dataset.getCurrentIndex();
  }

  /**
   * 获取数据索引区间，开闭 [,)
   *
   * @version v0.0.2+
   */
  getIndexRange() {
    return this._dataset.getIndexRange();
  }

  /**
   * 设置数据索引区间，开闭 [,)
   *
   * - 如果当前位置的索引不在设置的范围内，则自动跳到最大索引处并暂停播放
   *
   * @version v0.2.7+
   */
  setIndexRange(indexRange: [number, number]) {
    this._dataset.update({ indexRange });

    if (!this._dataset.isInRangeIndex(this.getCurrentIndex())) {
      this._updateTo({
        dataIndex: this._dataset.getMaxIndex(),
        causeBy: 'apiSetIndexRange',
        actionId: uid()
      });
    } else {
      this._update();
    }
  }

  /**
   * 获取播放速度（倍速）
   *
   * @version v0.2.0+
   */
  getSpeed() {
    return Number(this.__speed);
  }

  /**
   * 设置播放速度（倍速）
   *
   * @version v0.2.0+
   */
  setSpeed(speed: Option['config']['animation']['speed']) {
    if (
      [THEME.MOBILE_SIMPLE, THEME.MOBILE_SIMPLE_DARK].includes(this._option?.theme) ||
      this._option?.config?.operation?.speed?.enable !== true
    ) {
      return;
    }
    if (typeof SPEED_TEXT_MAP[String(Number(speed))] === 'undefined') {
      throw new Error(
        `"config.animation.speed" must be one of: ${Object.keys(SPEED_TEXT_MAP).toString()}`
      );
    }
    this.__speed = String(Number(speed));

    Array.from(this._$dom.querySelector('.js-menu-list-content').children).forEach($elem => {
      if ($elem.dataset.speed === SPEED_TEXT_MAP[String(Number(speed))]) {
        $elem.classList.add(styles.selected);
      } else {
        $elem.classList.remove(styles.selected);
      }
    });
    if (!this._$dom.classList.contains(styles['theme-pc']) || Number(speed) != 1) {
      this._$dom.querySelector('.js-speed-adjustment-btn > span').textContent = `${
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        SPEED_TEXT_MAP[String(Number(speed))]
      }X`;
    } else {
      this._$dom.querySelector('.js-speed-adjustment-btn > span').textContent = '倍速';
    }

    // @ts-ignore
    this._dispatchEvent(EVENT_TYPE_MAP.CHANGE_SPEED, { speed: Number(speed) });
  }

  /**
   * 更新轴的刻度值
   */
  private __updateAxisTickValue() {
    const self = this;

    // * 刻度值（所有的）
    const $tickValuesContainer = self._$dom.querySelector<HTMLElement>('.js-tickValue-all');
    const $tickValues = self._$dom.querySelectorAll<HTMLElement>(
      '.js-tickValue-all .js-tickValue-tick'
    );
    let tickValueIndex = -1;

    // 先隐藏，布局完成后显示
    $tickValuesContainer.style.opacity = '0';

    self._dataset.getData(false).forEach(timeData => {
      // ! 刻度值不渲染次要刻度
      if (timeData.type !== 'main') {
        return;
      }
      tickValueIndex += 1;

      let position = self._scale.at(timeData.index);
      const $tickValue = $tickValues[tickValueIndex];

      if (self._dataset.isIndexMin(timeData.index, false)) {
        position -= self._scale.padding()[0];
      } else if (self._dataset.isIndexMax(timeData.index, false)) {
        position += self._scale.padding()[1];
      }

      $tickValue.style.left = `${position}px`;
    });

    /**
     * 避免刻度值重叠
     * - 首尾两个刻度值必须显示
     * - 根据最小间距判断是否重叠，重叠则隐藏
     * - (v0.3.0+)以第一个和第二个显示的标签之间隐藏标签个数为基准，隐藏后续标签，即实现 0 2 4 6 8 这样显示的刻度分布比较均匀的效果
     */
    // eslint-disable-next-line sonarjs/cognitive-complexity
    function layoutToAvoidOverlapping() {
      let tickValueIndexA = -1;
      // 最小间距
      const DEFAULT_MIN_SPACE = 8;
      let minSpace = self._option?.config?.axis?.tickValue?.minSpace;
      minSpace = Number.isNaN(Number(minSpace)) ? DEFAULT_MIN_SPACE : minSpace;
      // ! 默认中间的刻度值是居中显示的
      const HALF_WIDTH = 2;

      // 最后一个刻度值的起始位置
      const lastTickValuePositionStart =
        self._scale.at(self._dataset.getMaxIndex(false)) +
        self._scale.padding()[1] -
        $tickValues[$tickValues.length - 1].getBoundingClientRect().width;
      let prevVisibleTickValuePositionEnd = 0;

      /** 显示的刻度标签索引 */
      let showTickIndex = 0;
      /** 前两个显示的刻度标签之间隐藏的标签个数 */
      let hideBetweenTwoShow = 0;
      /** 当前两个显示的刻度标签之间已隐藏数量 */
      let currentHideNum = 0;

      // eslint-disable-next-line complexity
      self._dataset.getData(false).forEach((timeData, index) => {
        // ! 刻度值不渲染次要刻度
        if (timeData.type !== 'main') {
          return;
        }
        tickValueIndexA += 1;

        const position = self._scale.at(timeData.index);
        const $tickValue = $tickValues[tickValueIndexA];

        // 获取轴标签有效的包围盒数据bBox，display: none会导致getBoundingClientRect返回0数据
        const $tickValueDisplayable = $tickValue.style.display !== 'none';
        if (!$tickValueDisplayable) {
          $tickValue.style.display = 'block';
        }
        const bBox = $tickValue.getBoundingClientRect();
        // 还原轴标签元素的display状态
        if (!$tickValueDisplayable) {
          $tickValue.style.display = 'none';
        }

        // 抽样展示
        if (bBox.width === 0 && bBox.height === 0) {
          $tickValue.style.visibility = 'hidden';
          if (showTickIndex === 0) {
            hideBetweenTwoShow += 1;
          }
        } else if (self._dataset.isIndexMin(index, false)) {
          prevVisibleTickValuePositionEnd = -self._scale.padding()[0] + position + bBox.width;
        } else if (self._dataset.isIndexMax(index, false)) {
          showTickIndex += 1;
          currentHideNum = 0;
        } else {
          // ! 因为是居中显示的
          const positionStart = position - bBox.width / HALF_WIDTH;
          const positionEnd = position + bBox.width / HALF_WIDTH;

          // 与前一个重叠，或与最后一个重叠的情况下隐藏
          if (
            (showTickIndex > 0 && currentHideNum < hideBetweenTwoShow) ||
            positionStart - prevVisibleTickValuePositionEnd <= minSpace ||
            lastTickValuePositionStart - positionEnd <= minSpace
          ) {
            $tickValue.style.visibility = 'hidden';
            // 如果与前一个标签重叠或与最后一个标签重叠，则需要将元素移除文档流，不影响时间轴外其他部分布局
            $tickValue.style.display = 'none';

            if (showTickIndex === 0) {
              hideBetweenTwoShow += 1;
            } else {
              currentHideNum += 1;
            }
          } else {
            $tickValue.style.visibility = 'visible';
            $tickValue.style.display = 'block';
            prevVisibleTickValuePositionEnd = positionEnd;
            showTickIndex += 1;
            currentHideNum = 0;
          }
        }
      });
    }

    if (self._option?.config?.axis?.tickValue?.hideOverlap === false) {
      $tickValues.forEach($tickValue => {
        $tickValue.style.visibility = 'visible';
        $tickValue.style.display = 'block';
      });
      $tickValuesContainer.style.opacity = '1';

      return self;
    }

    self.__delayCallback({
      key: '__updateAxisTickValue.layoutToAvoidOverlapping',
      callback: () => {
        layoutToAvoidOverlapping();

        // 布局完成，显示
        $tickValuesContainer.style.opacity = '1';
      },
      force: ENFORCE_EVERY_INTERVAL
    });

    return self;
  }

  /**
   * 更新轴的刻度线
   */
  private __updateAxisTickLine(
    currentPosition: number,
    startPosition: number,
    endPosition: number
  ) {
    const self = this;
    const $tickLinesContainer = self._$dom.querySelector<HTMLElement>('.js-tickLine-container');
    const $tickLines = self._$dom.querySelectorAll<HTMLElement>('.js-tickLine-tick');

    // 先隐藏，布局完成后显示
    // @ts-ignore
    $tickLinesContainer.style.opacity = 0;

    // eslint-disable-next-line complexity
    self._dataset.getData(false).forEach((timeData, index) => {
      const axisX = self._scale.at(timeData.index);
      const point = self._scale.domain()[index];
      const $tickLine = $tickLines[index];

      $tickLine.style.transform = `translate(${axisX}px, -50%)`;

      if (point.type === 'sub') {
        $tickLine.classList.remove('js-tickLine-main-tick');
        $tickLine.classList.add('js-tickLine-sub-tick', styles['tickLine-sub-tick']);
      } else {
        $tickLine.classList.remove('js-tickLine-sub-tick', styles['tickLine-sub-tick']);
      }
      if (point.hide) {
        if (point.type === 'main') {
          $tickLine.classList.add(styles['tickLine-tick_hide']);
        } else {
          $tickLine.classList.add(styles['tickLine-sub-tick_hide']);
        }
      } else {
        $tickLine.classList.remove(styles['tickLine-tick_hide']);
        $tickLine.classList.remove(styles['tickLine-sub-tick_hide']);
      }

      // * 展示进度累积效果
      let outdated = false;
      if (self._option.config?.axis?.progress?.show === true) {
        if (startPosition <= point.position && point.position <= currentPosition) {
          outdated = true;
          $tickLine.classList.add(styles.outdated);
        } else {
          $tickLine.classList.remove(styles.outdated);
        }
      }

      // * 范围播放时的样式
      if (self._dataset.isRange()) {
        if (!outdated && !(startPosition <= point.position && point.position <= endPosition)) {
          $tickLine.classList.add(styles['out-range']);
        } else {
          $tickLine.classList.remove(styles['out-range']);
        }
      }

      //
    });

    // 布局完成，显示
    // @ts-ignore
    $tickLinesContainer.style.opacity = 1;

    return self;
  }

  /**
   * 显示轴的提示
   */
  private _showAxisTooltip(showTime?: number) {
    if (this.__timers._hideAxisTooltip?.timer) {
      clearTimeout(this.__timers._hideAxisTooltip.timer);
      this.__timers._hideAxisTooltip.timer = null;
    }

    const $tooltip = this._$dom.querySelector('.js-timelineOperation .js-tooltip-text');

    $tooltip.parentElement.style.visibility = 'visible';

    if (typeof showTime === 'number') {
      this.__delayCallback({
        key: '_hideAxisTooltip',
        callback: () => this._hideAxisTooltip(),
        delay: showTime
      });
    }
  }

  /**
   * 隐藏轴的提示
   */
  private _hideAxisTooltip() {
    const $tooltip = this._$dom.querySelector('.js-timelineOperation .js-tooltip-text');

    $tooltip.parentElement.style.visibility = 'hidden';
  }

  /**
   * 更新轴的提示
   */
  private __updateAxisTooltip(dataIndex: number, position: number) {
    const $tooltip = this._$dom.querySelector('.js-timelineOperation .js-tooltip-text');

    let labelInnerHTML = this._dataset.getDataItem(dataIndex).originTime;
    if (
      this._option.config?.axis?.tooltip?.formatter &&
      typeof this._option.config?.axis?.tooltip?.formatter === 'function'
    ) {
      labelInnerHTML = this._option.config?.axis?.tooltip?.formatter(
        this._option.data[dataIndex],
        dataIndex
      );
    }
    $tooltip.innerHTML = labelInnerHTML as string;

    const $tooltipWrapper = $tooltip.parentElement;
    const $tooltipArrow: HTMLElement = $tooltipWrapper.querySelector('.js-tooltip-arrow');
    const xAxisBoxBBox = this._$dom.querySelector('.js-x-axis-box').getBoundingClientRect();
    const xAxisBBox = this._$dom.querySelector('.js-x-axis').getBoundingClientRect();
    const tooltipWrapperBBox = $tooltipWrapper.getBoundingClientRect();
    const markerLeft = parseInt(
      getComputedStyle($tooltipWrapper.parentElement).getPropertyValue('left'),
      10
    );
    // 居中（鼠标位置与实际 left 相差宽度的 50%）
    let tooltipLeft = position - tooltipWrapperBBox.width / 2;

    // ! 保证显示在轴的盒子范围内
    if (tooltipLeft < xAxisBoxBBox.left - xAxisBBox.left) {
      tooltipLeft = xAxisBoxBBox.left - xAxisBBox.left;
    }
    if (
      tooltipLeft + tooltipWrapperBBox.width >
      xAxisBBox.width + (xAxisBoxBBox.right - xAxisBBox.right)
    ) {
      tooltipLeft =
        xAxisBBox.width + (xAxisBoxBBox.right - xAxisBBox.right) - tooltipWrapperBBox.width;
    }

    $tooltipWrapper.style.left = `${tooltipLeft - markerLeft}px`;

    let arrowOffset = 0;
    if (this._$dom.classList.contains(styles['theme-with-value'])) {
      if (position <= this._scale.at(0)) {
        arrowOffset = 2;
      } else if (position >= this._scale.at(this._scale.domain().length - 1)) {
        arrowOffset = -2;
      }
    }

    $tooltipArrow.style.left = `${
      position - markerLeft + -(tooltipLeft - markerLeft) + arrowOffset
    }px`;
  }

  /**
   * 更新轴的指示器按钮
   */
  private __updateAxisPointerMarker(dataIndex: number, position: number) {
    const $pointer: HTMLElement = this._$dom.querySelector('.js-timelineOperation .js-pointer');

    // ! 为了不遮住两端字体
    let paddingOuter = 5;
    if (this._$dom.classList.contains(styles['theme-with-value-outer'])) {
      if (this._$dom.classList.contains(styles['theme-pc'])) {
        paddingOuter =
          $pointer.querySelector<HTMLElement>('.js-marker').getBoundingClientRect().width / 2;
      } else {
        paddingOuter = 12;
      }
    }

    $pointer.style.left = `${Math.min(
      Math.max(paddingOuter, position),
      this._axisWidth - paddingOuter
    )}px`;

    this.__updateAxisTooltip(dataIndex, position);
  }

  /**
   * 更新轴起始范围的指示器按钮
   */
  private __updateAxisStartRangePointerMarker(dataIndexStart?: number, position?: number) {
    if (
      !this._dataset.isRange() ||
      this._option?.config?.axis?.action?.dataIndexRange?.disableStartBtn === true
    ) {
      return;
    }

    if (typeof dataIndexStart === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      dataIndexStart = this._dataset.getMinIndex();
    }
    if (typeof position === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      position = this._scale.at(dataIndexStart);
    }

    const $pointer: HTMLElement = this._$dom.querySelector(
      '.js-timelineOperation .js-start-range-pointer'
    );
    // ! 为了不遮住两端字体
    let paddingOuter = 5;
    if (this._$dom.classList.contains(styles['theme-with-value-outer'])) {
      if (this._$dom.classList.contains(styles['theme-pc'])) {
        paddingOuter =
          $pointer.querySelector<HTMLElement>('.js-marker').getBoundingClientRect().width / 2;
      } else {
        paddingOuter = 12;
      }
    }

    $pointer.style.left = `${Math.min(
      Math.max(paddingOuter, position),
      this._axisWidth - paddingOuter
    )}px`;

    // ! 左边索引限制达到最大时，将左边按钮置在上层
    if (this._dataset.getIndexRange()[0] >= this._dataset.getMaxIndex(false)) {
      $pointer.style.zIndex = '20';
    } else {
      $pointer.style.zIndex = '19';
    }

    this.__updateAxisTooltip(dataIndexStart, position);
  }

  /**
   * 更新轴的范围进度条
   */
  private __updateAxisProgressRangeBar(progressStart: number, progressEnd: number) {
    const progressContainer =
      this._$dom.querySelector<HTMLElement>('.js-progress-range').parentElement;

    if (this._dataset.isRange()) {
      // ! 当有 padding 存在时，第一个点和最后一个点的位置需要调整
      if (progressStart <= this._scale.at(this._dataset.getMinIndex())) {
        progressStart = this._scale.at(this._dataset.getMinIndex()) - this._scale.padding()[0];
      }
      if (progressStart >= this._scale.at(this._dataset.getMaxIndex())) {
        progressStart = this._scale.at(this._dataset.getMaxIndex()) + this._scale.padding()[0];
      }

      progressContainer.style.display = 'block';
      progressContainer.style.left = `${progressStart}px`;
      progressContainer.style.width = `${progressEnd - progressStart}px`;
    } else {
      progressContainer.style.display = 'none';
    }
  }

  /**
   * 更新轴的进度条
   */
  private __updateAxisProgressBar(progressRate: number, progressStart: number) {
    const progressContainer = this._$dom.querySelector<HTMLElement>('.js-progress').parentElement;

    if (this._option.config?.axis?.progress?.show === true) {
      // ! 当有 padding 存在时，第一个点的位置不是最开始的位置
      if (progressStart <= this._scale.at(this._dataset.getMinIndex())) {
        progressStart = this._scale.at(this._dataset.getMinIndex()) - this._scale.padding()[0];
      }

      progressContainer.style.display = 'block';
      progressContainer.style.left = `${progressStart}px`;
      progressContainer.style.width = `${progressRate - progressStart}px`;
    } else {
      progressContainer.style.display = 'none';
    }
  }

  /**
   * 更新轴
   */
  private __updateAxis(
    dataIndex: number,
    {
      progressRate,
      dataIndexStart,
      progressStart,
      progressEnd
    }: {
      progressRate?: number;
      dataIndexStart?: number;
      progressStart?: number;
      progressEnd?: number;
    } = {}
  ) {
    if (typeof progressRate === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      progressRate = this._scale.at(dataIndex);
    }
    if (typeof dataIndexStart === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      dataIndexStart = this._dataset.getMinIndex();
    }
    if (typeof progressStart === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      progressStart = this._scale.at(this._dataset.getMinIndex());
    }
    if (typeof progressEnd === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      progressEnd = this._scale.at(this._dataset.getMaxIndex());
    }

    this.__updateAxisProgressRangeBar(progressStart, progressEnd);
    this.__updateAxisProgressBar(progressRate, progressStart);
    this.__updateAxisStartRangePointerMarker(dataIndexStart, progressStart);
    this.__updateAxisPointerMarker(dataIndex, progressRate);
    this.__updateAxisTickLine(progressRate, progressStart, progressEnd);
    // this.__updateAxisTickValue(); // 不需要每次都更新，但 resize 时候必须更新
  }

  /**
   * 切换操作按钮的显示状态
   */
  private __switchOperationButton(type: string) {
    Array.from(this._$dom.querySelectorAll<HTMLElement>('.js-processControl')).forEach(element => {
      if (element.classList.contains(type)) {
        // eslint-disable-next-line no-param-reassign
        element.style.display = 'block';
      } else {
        // eslint-disable-next-line no-param-reassign
        element.style.display = 'none';
      }
    });
  }

  /**
   * 更新控制按钮
   */
  private __updateControlButton(dataIndex: number) {
    this._$dom
      .querySelector('.js-timeline-btn-prev')
      ?.classList?.remove(styles['btn-disabled'], 'js-btn-disabled');
    this._$dom
      .querySelector('.js-timeline-btn-next')
      ?.classList?.remove(styles['btn-disabled'], 'js-btn-disabled');

    // 第一个时间周期，将“上一期”按钮置为disable
    if (this._dataset.isIndexMin(dataIndex)) {
      this._$dom
        .querySelector('.js-timeline-btn-prev')
        ?.classList?.add(styles['btn-disabled'], 'js-btn-disabled');
    }

    // 播放结束后，操控按钮变为播放，“下一期”按钮置为disable
    if (this._dataset.isIndexMax(dataIndex)) {
      this.__switchOperationButton('play');
      this._$dom
        .querySelector('.js-timeline-btn-next')
        ?.classList?.add(styles['btn-disabled'], 'js-btn-disabled');
    }
  }

  /**
   * 更新
   */
  protected _update() {
    // DOM 还没准备好
    if (!this._initialized) {
      this.__delayCallback({
        key: '_update',
        callback: () => this._update()
      });

      return;
    }
    debug('before update');

    const currentDataIndex = this._dataset.getCurrentIndex();

    this.__updateAxis(currentDataIndex);
    this.__updateControlButton(currentDataIndex);

    this.__render();

    debug('after update');
  }

  /**
   * 更新到指定位置
   */
  protected _updateTo(status: InstanceType<typeof Timeline>['__playQueue'][number]) {
    // * 播放中的话先暂停
    if (this.isPlaying()) {
      // 必然不会是 play
      // @ts-ignore
      this._pause({ causeBy: status.causeBy });
    }

    // 立即刷新时间轴状态
    this._dataset.update({ currentIndex: status.dataIndex });
    this._update();
    this._showAxisTooltip(1e3);

    // ! 重置队列
    this.__playQueue = [status];
  }

  /**
   * 更新时间轴到指定位置并暂停
   */
  update(dataIndex: number) {
    debug('update dataIndex: %d', dataIndex);

    if (!this._dataset.isValidIndex(dataIndex)) {
      throw new Error(`is not a valid time data index: ${dataIndex}`);
    }

    this._updateTo({ dataIndex, causeBy: 'apiUpdate', actionId: uid() });
  }

  /**
   * 渲染自定义样式
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  private __renderCustomStyles(timelineContextId: string) {
    const self = this;
    const $style = document.createElement('style');
    self.__tempStorage.set('styleTag', $style);
    let cssText = ``;
    const tasks = [
      {
        selector: '.js-x-axis-box .js-x-axis',
        styles: self._option?.config?.axis?.background?.style
      },
      {
        selector: '.js-x-axis-box .js-progress-range',
        styles: self._option?.config?.axis?.progressRange?.style
      },
      {
        selector: '.js-x-axis-box .js-progress',
        styles: self._option?.config?.axis?.progress?.style
      },
      {
        selector: '.js-x-axis-box .js-tickLine-main-tick',
        styles: self._option?.config?.axis?.tickLine?.main?.style
      },
      {
        selector: '.js-x-axis-box .js-tickLine-sub-tick',
        styles: self._option?.config?.axis?.tickLine?.sub?.style
      },
      {
        selector: '.js-x-axis-box .js-tickValue-tick > span',
        styles: self._option?.config?.axis?.tickValue?.style
      },
      {
        selector: '.js-x-axis-box .js-marker',
        styles: self._option?.config?.axis?.marker?.style
      },
      {
        selector: '.js-x-axis-box .js-tooltip-wrapper',
        styles: self._option?.config?.axis?.tooltip?.style
      },
      {
        selector: '.js-x-axis-box .js-tooltip-arrow',
        styles: self._option?.config?.axis?.tooltip?.arrowStyle
      },
      {
        selector: '.js-x-axis-box .js-tooltip-text',
        styles: self._option?.config?.axis?.tooltip?.textStyle
      },
      {
        selector: '.js-operation-box .js-timeline-btn-play',
        styles: self._option?.config?.operation?.playButton?.style
      },
      {
        selector: '.js-operation-box .js-timeline-btn-replay',
        styles: self._option?.config?.operation?.replayButton?.style
      },
      {
        selector: '.js-operation-box .js-timeline-btn-pause',
        styles: self._option?.config?.operation?.pauseButton?.style
      },
      {
        selector: '.js-operation-box .js-timeline-btn-prev',
        styles: self._option?.config?.operation?.previousButton?.style
      },
      {
        selector: '.js-operation-box .js-timeline-btn-next',
        styles: self._option?.config?.operation?.nextButton?.style
      },
      {
        selector: '.js-operation-box .js-speed-adjustment-btn',
        styles: self._option?.config?.operation?.speed?.style
      },
      {
        selector: '.js-operation-box .js-speed-adjustment-btn > span',
        styles: self._option?.config?.operation?.speed?.textStyle
      },
      {
        selector: '.js-operation-box .js-speed-adjustment-btn > label',
        styles: self._option?.config?.operation?.speed?.labelStyle
      },
      {
        selector: '.js-operation-box .js-background-mask',
        styles: self._option?.config?.operation?.speed?.menuListBackgroundMaskStyle
      },
      {
        selector: '.js-operation-box .js-menu-list-content',
        styles: self._option?.config?.operation?.speed?.menuListStyle
      },
      {
        selector: '.js-operation-box .js-menu-list-content > li',
        styles: self._option?.config?.operation?.speed?.menuListItemStyle
      },
      {
        selector: '.js-operation-box .js-menu-list-content > li > span',
        styles: self._option?.config?.operation?.speed?.menuListItemTextStyle
      },
      {
        selector: '.js-x-axis-box .js-unsupported-text',
        styles: self._option?.config?.axis?.unsupportedText?.style
      }
    ];

    function cssStyle(selector: string, cssStyles: CSSStyleDeclaration) {
      const css: string[] = [];
      Object.entries(cssStyles).forEach(([styleProp, stylePropValue]) => {
        if (styleProp.startsWith(':')) {
          if (
            styleProp.match(/:hover/i) &&
            [
              '.js-x-axis-box .js-x-axis',
              '.js-x-axis-box .js-tickLine-main-tick',
              '.js-x-axis-box .js-tickLine-sub-tick'
            ].includes(selector)
          ) {
            cssStyle(
              selector.replace('.js-x-axis-box', `.js-x-axis-box.${styles.hover}`),
              stylePropValue
            );
          } else if (
            styleProp.match(/:disabled/i) &&
            [
              '.js-operation-box .js-timeline-btn-prev',
              '.js-operation-box .js-timeline-btn-next'
            ].includes(selector)
          ) {
            cssStyle(`${selector}.${styles['btn-disabled']}`, stylePropValue);
          } else {
            cssStyle(`${selector}${styleProp}`, stylePropValue);
          }
        } else {
          css.push(`${capitalToDash(styleProp)}: ${stylePropValue}`);
        }
      });

      cssText += `#${timelineContextId} ${selector} {
        ${css.join(';')}
      }`;
    }

    tasks.forEach(item => {
      if (item.styles) {
        cssStyle(item.selector, item.styles);
      }
    });

    if ($style.styleSheet) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      $style.styleSheet.cssText = cssText;
    } else {
      $style.appendChild(document.createTextNode(cssText));
    }

    if (cssText) {
      document.getElementsByTagName('head')[0].appendChild($style);
    }

    return self;
  }

  /**
   * 渲染
   */
  // eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity, complexity
  private __render() {
    debug('render');

    // ! 只渲染一次，后续只更新即可
    if (this._initialized) {
      return;
    }

    const self = this;
    const isPCTheme = self._$dom.classList.contains(styles['theme-pc']);
    const timelineContextId = `timeline-ctx-${uid()}`;

    self._$dom.innerHTML = buildDomHelper.buildTimeline({
      id: timelineContextId,
      styles,
      isPCTheme,
      option: this._option,
      data: this._dataset.getData(false),
      isRange: self._dataset.isRange(),
      speed: this.__speed
    });

    this.__renderCustomStyles(timelineContextId);

    if (this._option.data.length > 1) {
      this.__registerAction();
      this.__bindEvents();
    }
  }

  /**
   * 注册交互行为事件
   */
  private __registerAction() {
    const self = this;

    self._action.on('play', () => {
      self.play();
    });

    self._action.on('replay', () => {
      self.play(self._dataset.getMinIndex());
    });

    self._action.on('pause', () => {
      self._pause({ causeBy: 'pause' });
    });

    self._action.on('previous', () => {
      if (self._dataset.isIndexMin()) {
        return;
      }

      const prevDataIndex = self._dataset.getCurrentIndex() - 1;

      self._updateTo({ dataIndex: prevDataIndex, causeBy: 'previous', actionId: uid() });

      //
    });

    self._action.on('next', () => {
      // FIXME
      if (self._dataset.isIndexMax(self._dataset.getCurrentIndex(), true)) {
        return;
      }

      const nextDataIndex = self._dataset.getCurrentIndex() + 1;

      self._updateTo({ dataIndex: nextDataIndex, causeBy: 'next', actionId: uid() });

      //
    });
  }

  /**
   * 绑定交互事件
   */
  private __bindEvents() {
    const self = this;

    function handler(e) {
      // 播放
      if (e.target.classList.contains('play')) {
        self.dispatchAction('play');
      } else if (e.target.classList.contains('replay')) {
        self.dispatchAction('replay');
      } else if (e.target.classList.contains('pause')) {
        self.dispatchAction('pause');
      } else if (e.target.classList.contains('previous')) {
        self.dispatchAction('previous');
      } else if (e.target.classList.contains('next')) {
        self.dispatchAction('next');
      }
    }

    Array.from(self._$dom.querySelectorAll('.js-operationButton')).forEach(element => {
      element.addEventListener('click', handler);
    });

    // * 倍速 ------------------------
    self._$dom.querySelector('.js-speed-adjustment-btn').addEventListener(
      'click',
      event => {
        // if (!event.currentTarget.classList.contains('js-speed-adjustment-btn')) {
        //   return;
        // }

        self._$dom.querySelector('.js-menu-list').classList.toggle(styles.show);
      },
      false
    );

    self._$dom.querySelector('.js-menu-list').addEventListener(
      'click',
      event => {
        const { speed } = event.target.dataset;
        if (typeof speed === 'undefined') {
          return;
        }

        self.setSpeed(speed);
      },
      false
    );
    // ------------------------

    self.__registerActionForPointer();
  }

  // eslint-disable-next-line max-lines-per-function
  private __registerActionForPointer() {
    const self = this;
    const $pointerMarker: HTMLElement = self._$dom.querySelector('.js-pointer .js-marker');
    const $startRangePointerMarker: HTMLElement = self._$dom.querySelector(
      '.js-start-range-pointer .js-marker'
    );

    /**
     * 获取时间轴上，当前鼠标位置对应的X值
     */
    function getCurrentX(event) {
      const range = self._scale.range();
      const paddingOuter = self._scale.padding();
      let touch;
      if (event.touches) {
        // eslint-disable-next-line prefer-destructuring
        touch = event.touches[0];
      } else {
        touch = event;
      }

      // ! 用可视区的坐标计算
      let x =
        touch.clientX -
        self._$dom.querySelector('.js-interaction-area').getBoundingClientRect().left;
      if (x < range[0] + paddingOuter[0]) {
        // eslint-disable-next-line prefer-destructuring
        x = range[0] + paddingOuter[0];
      } else if (x > range[1] - paddingOuter[1]) {
        // eslint-disable-next-line prefer-destructuring
        x = range[1] - paddingOuter[1];
      }

      return x;
    }

    let flag = false;
    let lastMoveEnd = 0;
    let hoverDataIndex: number;
    let actionId = '';

    function down(e, action: 'axisMarkerDrag' | 'axisClick') {
      debug(`${action} start`);

      // * 播放中的话先暂停
      if (self.isPlaying()) {
        self._pause({ causeBy: action });
      }

      flag = true;
      // 重置
      hoverDataIndex = self._dataset.getCurrentIndex();
      actionId = uid();

      self._showAxisTooltip();
      self._$dom.querySelector('.js-x-axis-box').classList.add(styles.hover);
    }

    // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
    function move(
      event_: MouseEvent,
      $moveElem?: HTMLElement,
      action?: 'axisMarkerDrag' | 'axisClick'
    ) {
      if (!flag) {
        return;
      }

      const event = event_ || window.event;
      let x = getCurrentX(event);
      const dataIndex = self._scale.findPointAt(x).index;
      hoverDataIndex = dataIndex;

      if ($moveElem === $pointerMarker) {
        // * 计算区间
        if (dataIndex <= self._dataset.getMinIndex()) {
          hoverDataIndex = self._dataset.getMinIndex();
          x = Math.max(x, self._scale.at(hoverDataIndex));
        }
        if (
          self._dataset.isRange() &&
          self._option?.config?.axis?.action?.dataIndexRange?.disableEndBtnChangeRange === true &&
          dataIndex >= self._dataset.getMaxIndex()
        ) {
          hoverDataIndex = self._dataset.getMaxIndex();
          x = Math.min(x, self._scale.at(hoverDataIndex));
        }
        // * 拖动交互过程中，让轴实时更新，而不是只能停留在某个点刻度处
        self.__updateAxis(hoverDataIndex, {
          progressRate: x,
          progressEnd:
            self._dataset.isRange() &&
            !self._option?.config?.axis?.action?.dataIndexRange?.disableEndBtnChangeRange
              ? x
              : undefined
        });

        // 拖动过程中实时触发 change
        if (
          action === 'axisMarkerDrag' &&
          self._option.config.axis?.action?.enableDragChange === true
        ) {
          // @ts-ignore
          self._dispatchEvent(EVENT_TYPE_MAP.CHANGE, {
            index: hoverDataIndex,
            actionId,
            causeBy: 'axisMarkerDrag'
          });
        }
      } else {
        // * 计算区间
        if (dataIndex >= self._dataset.getMaxIndex()) {
          hoverDataIndex = self._dataset.getMaxIndex();
          x = Math.min(x, self._scale.at(hoverDataIndex));
        }
        // * 拖动交互过程中，让轴实时更新，而不是只能停留在某个点刻度处
        self.__updateAxis(self._dataset.getCurrentIndex(), {
          dataIndexStart: hoverDataIndex,
          progressStart:
            self._dataset.isRange() &&
            !self._option?.config?.axis?.action?.dataIndexRange?.disableStartBtn
              ? x
              : undefined
        });
        // HACK 刷新 tooltip 的位置
        self.__updateAxisStartRangePointerMarker(hoverDataIndex, x);
      }
    }

    // 鼠标释放时候的函数
    function end($moveElem?: HTMLElement, action?: 'axisMarkerDrag' | 'axisClick') {
      debug(`${action} end`);

      if (action === 'axisMarkerDrag') {
        lastMoveEnd = Date.now();
      }
      flag = false;

      self.__delayCallback({
        key: '_hideAxisTooltip',
        callback: () => self._hideAxisTooltip(),
        delay: 1e3
      });

      if ($moveElem === $pointerMarker) {
        if (self._dataset.getCurrentIndex() !== hoverDataIndex) {
          if (
            self._dataset.isRange() &&
            !self._option?.config?.axis?.action?.dataIndexRange?.disableEndBtnChangeRange
          ) {
            // * 更新区间
            self._dataset.update({
              indexRange: [self._dataset.getIndexRange()[0], hoverDataIndex + 1]
            });
          }
          self._updateTo({ dataIndex: hoverDataIndex, causeBy: action, actionId });
        } else {
          self._update();
        }
      } else {
        if (
          self._dataset.isRange() &&
          self._dataset.getIndexRange()[0] !== hoverDataIndex &&
          !self._option?.config?.axis?.action?.dataIndexRange?.disableStartBtn
        ) {
          // * 更新区间
          self._dataset.update({
            indexRange: [hoverDataIndex, self._dataset.getIndexRange()[1]]
          });
        }
        self._update();
        // HACK 刷新 tooltip 的位置
        self.__updateAxisStartRangePointerMarker();
      }

      self._$dom.querySelector('.js-x-axis-box').classList.remove(styles.hover);
    }

    /**
     * 移动
     */
    function moveTooltip(event_) {
      const event = event_ || window.event;
      const x = getCurrentX(event);

      const dataIndex = self._scale.findPointAt(x).index;

      self.__updateAxisTooltip(dataIndex, x);
    }

    /**
     * 仅用于PC端的悬浮交互，只出现悬浮小tooltip
     */
    function mousemoveHandler(e: MouseEvent) {
      moveTooltip(e);
      self._showAxisTooltip();

      self._$dom.querySelector('.js-x-axis-box').classList.add(styles.hover);
    }

    function mouseleaveHandler(e: MouseEvent) {
      self.__delayCallback({
        key: '_hideAxisTooltip',
        callback: () => self._hideAxisTooltip(),
        delay: 1e3
      });

      self._$dom.querySelector('.js-x-axis-box').classList.remove(styles.hover);
    }

    // * 时间轴热区的点击交互事件绑定
    self._$dom.querySelector('.js-interaction-area').onclick = (event: MouseEvent) => {
      if (
        self._option?.config?.axis?.action?.clickDisabled ||
        event.target === $startRangePointerMarker ||
        event.target.classList.contains('js-start-range-pointer')
      ) {
        return;
      }

      // HACK 防止移动的同时触发点击事件
      if (Date.now() - lastMoveEnd < 100) {
        return;
      }

      // flag = true;
      down(event, 'axisClick');
      move(event, $pointerMarker, 'axisClick');
      end($pointerMarker, 'axisClick');
    };

    function registerPCEventHandler() {
      self._$dom.querySelector('.js-interaction-area').onmousemove = mousemoveHandler;
      self._$dom.querySelector('.js-interaction-area').onmouseleave = mouseleaveHandler;
    }
    registerPCEventHandler();

    [$pointerMarker, $startRangePointerMarker].forEach($el => {
      // * 移动端事件绑定
      $el.addEventListener(
        'touchstart',
        event => {
          down(event, 'axisMarkerDrag');
        },
        false
      );

      $el.addEventListener(
        'touchmove',
        event => {
          // 阻止页面的滑动默认事件
          event?.preventDefault();

          move(event, $el, 'axisMarkerDrag');
        },
        false
      );
      $el.addEventListener(
        'touchend',
        () => {
          end($el, 'axisMarkerDrag');
        },
        false
      );

      /**
       * * PC 端事件绑定
       * 注意：marker很小，因此move事件应该绑定在热区上，否则用户很容易移出marker区域
       */
      $el.addEventListener(
        'mousedown',
        e => {
          down(e, 'axisMarkerDrag');

          function handleMousemove(event: MouseEvent) {
            // 避免滑动过程中，误选中其他元素
            event.preventDefault();

            move(event, $el, 'axisMarkerDrag');
          }

          // 操作热区事件的时候，记得把marker的事件响应(point-events)取消掉，否则会遮挡热区事件
          $el.style.pointerEvents = 'none';
          function endMove() {
            document.body.removeEventListener('mousemove', handleMousemove);
            document.body.removeEventListener('mouseup', endMove);

            end($el, 'axisMarkerDrag');
            // 释放鼠标或者超出热区后，需要恢复热区默认的事件，以及marker的事件响应
            registerPCEventHandler();

            self._$dom.querySelector<HTMLElement>('.js-interaction-area').onmouseup = null;
            $el.style.pointerEvents = 'auto';
          }

          document.body.addEventListener('mousemove', handleMousemove);
          document.body.addEventListener('mouseup', endMove);

          self._$dom.querySelector<HTMLElement>('.js-interaction-area').onmousemove = null;
          self._$dom.querySelector<HTMLElement>('.js-interaction-area').onmouseleave = null;
          self._$dom.querySelector<HTMLElement>('.js-interaction-area').onmouseup = endMove;
        },
        false
      );

      //
    });

    //
  }

  /**
   * 动画主循环
   * ! 此循环用来控制外部图表组件的更新调度
   * 消费播放队列
   * 为了避免用户在上一个动画播放到一半的时候，拖动滑块，导致上一周期动画计算未完成的问题
   * 这里用一个队列来存储用户的操作，每次执行的时候，只取最后一个周期进行绘制
   * 这样每次用户拖动滑块，最多绘制2次动画周期
   */
  private __loop() {
    if (this.__playQueue.length > 0) {
      const lastAction = this.__playQueue.shift();
      const needPendingAnimation =
        typeof this._option?.config?.animation?.causeBy === 'undefined' ||
        this._option.config.animation.causeBy.includes(lastAction?.causeBy);

      if (!needPendingAnimation || !this.__pendingTimer) {
        // 刷新状态
        if (this._dataset.getCurrentIndex() !== lastAction.dataIndex) {
          this._dataset.update({ currentIndex: lastAction.dataIndex });
        }
        this._update();

        // FIXME
        // * 通知图表进行下一个阶段动画
        // @ts-ignore
        this._dispatchEvent(EVENT_TYPE_MAP.CHANGE, {
          causeBy: lastAction?.causeBy,
          actionId: lastAction?.actionId
        });

        // * 播放中的话触发暂停事件
        if (this.isPlaying() && this.__playQueue.length <= 0) {
          // @ts-ignore
          this._pause();
        }

        if (this.__pendingTimer) {
          clearTimeout(this.__pendingTimer);
          this.__pendingTimer = null;
        }
        if (needPendingAnimation) {
          // ! 根据配置的时间等待图表动画完成再进行下一次状态刷新
          this.__pendingTimer = setTimeout(() => {
            this.__pendingTimer = null;
          }, this._option.config.animation.intervalTime / Number(this.__speed));
        }
      } else {
        this.__playQueue.unshift(lastAction);
      }
    }

    this.__loopTimer = setTimeout(() => {
      this.__loop();
    }, Timeline.CONSUME_INTERVAL);
  }

  /**
   * 暂停主循环
   */
  private __stopLoop() {
    if (this.__loopTimer) {
      clearTimeout(this.__loopTimer);
      this.__loopTimer = null;
    }
    if (this.__pendingTimer) {
      clearTimeout(this.__pendingTimer);
      this.__pendingTimer = null;
    }
  }

  /**
   * 开始播放（播放到最后一个数据时，会自动暂停，不会重新从头开始播放。）
   *
   * - 没有 `dataIndex` 参数时，会从下一个索引处开始播放，若当前已处于最大索引处，则从小索引开始播放
   * - 传了 `dataIndex` 参数时，先跳到指定时间节点，再开始播放
   */
  play(dataIndex?: number) {
    debug('play dataIndex: %s', dataIndex);

    const self = this;

    if (typeof dataIndex !== 'undefined') {
      if (dataIndex === self._dataset.getCurrentIndex() && self.isPlaying()) {
        return;
      }

      self._dataset.update({ currentIndex: dataIndex });
      self._update();
    } else {
      if (self.isPlaying()) {
        return;
      }

      self._dataset.update({
        currentIndex: self._dataset.isIndexMax()
          ? self._dataset.getMinIndex()
          : self._dataset.getCurrentIndex() + 1
      });
      self._update();
    }

    let toBePlayed = [];
    toBePlayed = self._dataset
      .getData(false)
      .map(item => item.index)
      .slice(self._dataset.getCurrentIndex(), self._dataset.getIndexRange()[1]);

    if (toBePlayed.length <= 0) {
      return;
    }

    self._playing = true;
    // ! 重置队列
    self.__playQueue = toBePlayed.map(i => ({ dataIndex: i, causeBy: 'play' }));

    self.__switchOperationButton('pause');
    // @ts-ignore
    self._dispatchEvent(EVENT_TYPE_MAP.PLAY);
  }

  protected _pause(...args: Parameters<EventDefinition[typeof EVENT_TYPE_MAP.PAUSE]>) {
    if (!this.isPlaying()) {
      return;
    }

    this._playing = false;
    // ! 清空队列
    this.__playQueue = [];

    this.__switchOperationButton('play');
    this._dispatchEvent(EVENT_TYPE_MAP.PAUSE, ...args);
  }

  /**
   * 暂停动画
   */
  pause() {
    this._pause({ causeBy: 'pause' });
  }

  /**
   * 是否播放中
   */
  isPlaying() {
    return this._playing;
  }

  private _resize() {
    debug('resize');

    const self = this;
    const cssStyleDeclaration = getComputedStyle(self._$dom);

    self.width = parseInt(cssStyleDeclaration.getPropertyValue('width'), 10);
    self.height = parseInt(cssStyleDeclaration.getPropertyValue('height'), 10);
    if (self._option.data.length <= 1) {
      return;
    }

    self._axisWidth = parseInt(
      getComputedStyle(self._$dom.querySelector('.js-x-axis')).getPropertyValue('width'),
      10
    );

    if (self._axisWidth === null) {
      self.__delayCallback({
        key: 'resize-retry',
        callback: () => {
          self._resize();
        }
      });

      return;
    }
    self._scale.range([0, self._axisWidth]);

    self._update();
    // ! 不需要每次都更新，但 resize 时候必须更新 --------
    self.__updateAxisTickValue();
  }

  /**
   * 自适应
   */
  resize() {
    const self = this;

    // * 先执行的节流操作，保证交互的响应性
    self.__delayCallback({
      key: 'resize',
      callback: () => {
        self._resize();
      },
      force: ENFORCE_EVERY_INTERVAL,
      leading: true
    });
    // * 最后一次的操作，保证数据是最新的
    self.__delayCallback({
      key: 'resize-finished',
      callback: () => {
        self._resize();
      }
    });
  }

  /**
   * 分发交互行为事件
   */
  dispatchAction<A extends keyof ActionDefinition>(
    action: A,
    ...args: Parameters<ActionDefinition[A]>
  ) {
    if (this._action.hasType(action)) {
      this._action.emit(action, ...args);
    }
  }

  /**
   * 分发事件
   */
  protected _dispatchEvent<E extends keyof EventDefinition>(
    event: E,
    ...args: Parameters<EventDefinition[E]>
  ) {
    const currentDataIndex = this._dataset.getCurrentIndex();
    let eventData = {
      type: event,
      index: currentDataIndex,
      timeData:
        this._option.data[
          typeof args[0]?.index !== 'undefined' ? args[0]?.index : currentDataIndex
        ],
      speed: Number(this.__speed)
    };

    // eventData
    // @ts-ignore
    if (args.length !== 0 && args[0]) {
      // @ts-ignore
      eventData = { ...eventData, ...args[0] };
    }

    // ! change 事件不会在索引没有发生变动时触发
    if (event === EVENT_TYPE_MAP.CHANGE) {
      if (this.__tempStorage.get('changeDataIndex') === eventData.index) {
        return;
      }
      this.__tempStorage.set('changeDataIndex', eventData.index);
    }

    // @ts-ignore
    this.emit(event, eventData, ...args.slice(1));
    debug('dispatchEvent %s %o', event, [eventData, ...args.slice(1)]);
  }
}
